// src/pages/Projects/ProjectPhaseList.jsx
// -----------------------------------------------------------------------------
// Component screen managing Project Phases (Project_Phase_TBL) using reusable components.
// Performs full CRUD (Read, Create, Update, Delete) locally or via projectService.
// -----------------------------------------------------------------------------
import { useEffect, useState, useMemo } from 'react';
import projectService from '../../api/projectService';
import useTableData from '../../hooks/useTableData';
import TableList from '../../components/common/TableList';
import TableForm from '../../components/common/TableForm';
import DeleteConfirmModal from '../../components/common/DeleteConfirmModal';
import StatusBadge from '../../components/common/StatusBadge';

export default function ProjectPhaseList() {
  const [projects, setProjects] = useState([]);

  // Fetch projects to map names and populate select dropdown options
  useEffect(() => {
    projectService
      .getAll()
      .then((res) => setProjects(res.data || []))
      .catch(() => {});
  }, []);

  // Adapt projectService phase operations to fit standard CRUD format
  const phaseServiceAdapter = useMemo(() => ({
    getAll: () => projectService.getAllPhases(),
    create: (payload) => projectService.createPhaseDirect(payload),
    update: (phaseID, payload) => projectService.updatePhaseDirect(phaseID, payload),
    remove: (phaseID) => projectService.removePhaseDirect(phaseID),
  }), []);

  const {
    data,
    paginatedData,
    loading,
    submitting,
    deleting,
    serverErrors,
    searchTerm,
    setSearchTerm,
    sortConfig,
    handleSort,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalPages,
    totalRecords,
    isFormOpen,
    isDeleteOpen,
    formMode,
    selectedRecord,
    openCreateModal,
    openEditModal,
    closeFormModal,
    openDeleteModal,
    closeDeleteModal,
    handleSave,
    handleDelete,
  } = useTableData(phaseServiceAdapter, {
    idKey: 'phaseID',
    itemName: 'Project Phase',
    initialSort: { key: 'phaseID', direction: 'asc' },
  });

  // Schema configuration for Project_Phase_TBL
  const phaseFields = useMemo(() => [
    {
      name: 'phaseID',
      label: 'ID',
      type: 'integer',
      isPrimaryKey: true,
      readOnly: true,
    },
    {
      name: 'projectID',
      label: 'Project',
      type: 'select',
      required: true,
      options: projects.map((p) => ({ label: `${p.projectCode} — ${p.projectName}`, value: p.projectID })),
    },
    {
      name: 'phaseName',
      label: 'Phase Name',
      type: 'varchar',
      required: true,
      maxLength: 100,
      placeholder: 'e.g. Planning, Design, Development, Testing',
    },
    {
      name: 'startDate',
      label: 'Start Date',
      type: 'date',
      required: true,
    },
    {
      name: 'endDate',
      label: 'End Date',
      type: 'date',
      required: true,
    },
    {
      name: 'status',
      label: 'Status',
      type: 'select',
      defaultValue: 'Active',
      options: [
        { label: 'Active', value: 'Active' },
        { label: 'Completed', value: 'Completed' },
        { label: 'Inactive', value: 'Inactive' },
      ],
    },
  ], [projects]);

  const columns = [
    { key: 'phaseID', label: 'ID', sortable: true, width: '80px' },
    {
      key: 'projectID',
      label: 'Project',
      sortable: true,
      render: (row) => {
        const proj = projects.find((p) => Number(p.projectID) === Number(row.projectID));
        return proj ? `${proj.projectCode} — ${proj.projectName}` : `-`;
      },
    },
    { key: 'phaseName', label: 'Phase Name', sortable: true },
    {
      key: 'startDate',
      label: 'Start Date',
      sortable: true,
      render: (row) => (row.startDate ? new Date(row.startDate).toLocaleDateString() : '-'),
    },
    {
      key: 'endDate',
      label: 'End Date',
      sortable: true,
      render: (row) => (row.endDate ? new Date(row.endDate).toLocaleDateString() : '-'),
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (row) => <StatusBadge value={row.status} />,
    },
  ];

  return (
    <div>
      <div className="page-header" style={{ marginBottom: '20px' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '24px' }}>Project Phases</h1>
          <div className="subtitle" style={{ color: 'var(--color-text-muted)', fontSize: '13px', marginTop: '4px' }}>
            Phases and lifecycles defined across all projects.
          </div>
        </div>
        <button className="btn btn-primary" onClick={openCreateModal}>
          + New Phase
        </button>
      </div>

      {/* Main Grid Table List */}
      <TableList
        columns={columns}
        data={data}
        paginatedData={paginatedData}
        loading={loading}
        idKey="phaseID"
        emptyMessage="No project phases found."
        // Search & Filter
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        // Sorting
        sortConfig={sortConfig}
        onSort={handleSort}
        // Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalRecords={totalRecords}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
        // Row Actions
        onEdit={openEditModal}
        onDelete={openDeleteModal}
      />

      {/* Reusable Form Modal */}
      {isFormOpen && (
        <TableForm
          fields={phaseFields}
          initialValues={selectedRecord || { projectID: '', phaseName: '', startDate: '', endDate: '', status: 'Active' }}
          mode={formMode}
          isOpen={isFormOpen}
          onSubmit={handleSave}
          onCancel={closeFormModal}
          submitting={submitting}
          serverErrors={serverErrors}
          itemName="Project Phase"
        />
      )}

      {/* Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={isDeleteOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDelete}
        title="Delete Project Phase"
        itemLabel={selectedRecord ? selectedRecord.phaseName : 'Project Phase'}
        deleting={deleting}
      />
    </div>
  );
}
